import {Fragment, type ReactNode, useEffect} from 'react';
import {Link} from 'react-router-dom';
import {ChevronRight, Lock, Webhook, X} from 'lucide-react';
import type {WebhookDeliveryStatus} from '../../../store/features/integrations/integrationsService';
import {type DeliveryKind, deliveryKind, type DeliveryKindInput, deliveryLabel, type TickTone, tickTone, type WebhookKind, type WebhookState} from './webhookModel';

const STATE_STYLE: Readonly<Record<WebhookState, {readonly label: string; readonly dot: string}>> = {
    active: {label: 'Active', dot: 'bg-success'},
    paused: {label: 'Paused', dot: 'bg-ink-disabled'},
    failing: {label: 'Failing', dot: 'bg-danger'},
};

export function StatusPill({state}: {readonly state: WebhookState}) {
    const style = STATE_STYLE[state];
    return <span className="inline-flex h-5 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface px-2 text-[11px] font-medium text-ink-secondary">
        <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`}/>{style.label}
    </span>;
}

export function EncryptedBadge() {
    return <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-line bg-surface-muted px-2 text-[11px] font-medium text-ink-secondary"><Lock size={10} className="!p-0"/>Encrypted</span>;
}

const CALLOUT_TONES = {
    neutral: 'border-line bg-surface-muted text-ink-secondary',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    error: 'border-danger-tint bg-danger-tint text-danger-ink',
} as const;

export function Callout({tone = 'neutral', icon, children}: {readonly tone?: keyof typeof CALLOUT_TONES; readonly icon: ReactNode; readonly children: ReactNode}) {
    return <div className={`flex items-start gap-3 rounded-lg border p-3 text-sm leading-5 ${CALLOUT_TONES[tone]}`}>
        <span className="mt-0.5 shrink-0">{icon}</span><div>{children}</div>
    </div>;
}

// Gleap masks the live value. Keep the secret out of attributes and text nodes
// as its screenshot serializer also clones those before upload.
// Grows with its content so long URLs are never clipped behind a scrollbar.
export function SecretText({label, value, className = ''}: {readonly label: string; readonly value: string; readonly className?: string}) {
    return <textarea aria-label={label} gleap-ignore="value" readOnly rows={1}
        className={`rr-mask block w-full resize-none overflow-hidden border-0 bg-transparent font-mono text-xs leading-5 outline-none ${className}`}
        ref={element => {
            if (!element) return;
            element.value = value;
            element.style.height = 'auto';
            element.style.height = `${element.scrollHeight}px`;
        }}/>;
}

type Tone = 'green' | 'red' | 'amber' | 'indigo' | 'grey';

const TONES: Readonly<Record<Tone, {readonly pill: string; readonly dot: string}>> = {
    green: {pill: 'border-success-tint bg-success-tint text-success-ink', dot: 'bg-success'},
    red: {pill: 'border-danger-tint bg-danger-tint text-danger-ink', dot: 'bg-danger'},
    amber: {pill: 'border-warning-line bg-warning-tint text-warning-ink', dot: 'bg-warning'},
    indigo: {pill: 'border-indigo-200 bg-indigo-50 text-indigo-700', dot: 'bg-indigo-500'},
    grey: {pill: 'border-line bg-zinc-50 text-ink-secondary', dot: 'bg-fill-subtle'},
};

export function Badge({tone, children}: {readonly tone: Tone; readonly children: ReactNode}) {
    return <span className={`inline-flex h-[22px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-xs font-medium ${TONES[tone].pill}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${TONES[tone].dot}`}/>{children}
    </span>;
}

const HEALTH_BADGES: Readonly<Record<WebhookKind, readonly [Tone, string]>> = {
    healthy: ['green', 'Healthy'],
    failing: ['red', 'Failing'],
    autoDisabled: ['grey', 'Auto-disabled'],
    off: ['grey', 'Off'],
};

export function HealthBadge({kind}: {readonly kind: WebhookKind}) {
    const [tone, label] = HEALTH_BADGES[kind];
    return <Badge tone={tone}>{label}</Badge>;
}

const DELIVERY_TONES: Readonly<Record<DeliveryKind, Tone>> = {
    delivered: 'green',
    failed: 'red',
    retrying: 'amber',
    queued: 'indigo',
    sending: 'indigo',
    waiting: 'grey',
};

export function DeliveryBadge({delivery}: {readonly delivery: DeliveryKindInput}) {
    return <Badge tone={DELIVERY_TONES[deliveryKind(delivery)]}>{deliveryLabel(delivery)}</Badge>;
}

export interface FilterPill<T extends string> {
    readonly id: T;
    readonly label: string;
    readonly count?: number;
}

export function FilterPills<T extends string>({label, items, value, onChange}: {readonly label: string; readonly items: readonly FilterPill<T>[]; readonly value: T; readonly onChange: (value: T) => void}) {
    return <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1">
        {items.map(item => {
            const active = item.id === value;
            return <button key={item.id} type="button" aria-pressed={active} onClick={() => onChange(item.id)}
                className={`inline-flex h-[30px] items-center gap-1.5 rounded-full px-3 text-[13px] font-medium transition-colors ${active ? 'bg-indigo-500 text-white' : 'text-ink-secondary hover:bg-zinc-100'}`}>
                {item.label}
                {item.count !== undefined && <span className={`text-xs ${active ? 'text-white/80' : 'text-ink-disabled'}`}>{item.count}</span>}
            </button>;
        })}
    </div>;
}

const TICK_COLORS: Readonly<Record<TickTone, string>> = {
    success: 'bg-success',
    failure: 'bg-danger',
    pending: 'bg-warning',
};

export function HealthTicks({statuses}: {readonly statuses: readonly WebhookDeliveryStatus[]}) {
    const failed = statuses.filter(status => status === 'FAILED').length;
    return <span role="img" aria-label={`Last ${statuses.length} deliveries, ${failed} failed`} className="flex items-center gap-0.5">
        {[...statuses].reverse().map((status, index) => <span key={index} className={`h-3.5 w-[3px] rounded-[1px] ${TICK_COLORS[tickTone(status)]}`}/>)}
    </span>;
}

export function ToggleTrack({checked, small = false}: {readonly checked: boolean; readonly small?: boolean}) {
    return <span aria-hidden className={`relative inline-block shrink-0 rounded-full transition-colors ${small ? 'h-[18px] w-8' : 'h-5 w-9'} ${checked ? 'bg-indigo-500' : 'bg-surface-muted'}`}>
        <span className={`absolute top-0.5 rounded-full bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-[left] ${small ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${checked ? (small ? 'left-4' : 'left-[18px]') : 'left-0.5'}`}/>
    </span>;
}

export function Toggle({label, checked, disabled, onChange}: {readonly label: string; readonly checked: boolean; readonly disabled?: boolean; readonly onChange: (checked: boolean) => void}) {
    return <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
        onClick={event => { event.stopPropagation(); onChange(!checked); }}
        className="flex py-1.5 disabled:cursor-not-allowed disabled:opacity-50">
        <ToggleTrack checked={checked}/>
    </button>;
}

export interface RadioCard<T extends string> {
    readonly value: T;
    readonly label: string;
    readonly description: string;
}

export function RadioCardList<T extends string>({label, options, value, onChange}: {readonly label: string; readonly options: readonly RadioCard<T>[]; readonly value: T | undefined; readonly onChange: (value: T) => void}) {
    return <div role="radiogroup" aria-label={label} className="overflow-hidden rounded-[10px] border border-line shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        {options.map((option, index) => {
            const selected = option.value === value;
            return <button key={option.value} type="button" role="radio" aria-checked={selected} onClick={() => onChange(option.value)}
                className={`box-border flex w-full items-center gap-3.5 px-4 py-3 text-left ${index > 0 ? 'border-t border-line' : ''} ${selected ? 'bg-indigo-50/60' : 'bg-surface hover:bg-zinc-50'}`}>
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink">{option.label}</span>
                    <span className="mt-0.5 block text-[13px] text-ink-muted">{option.description}</span>
                </span>
                <span aria-hidden className={`h-[18px] w-[18px] shrink-0 rounded-full bg-surface ${selected ? 'border-[5px] border-indigo-500' : 'border-[1.5px] border-line'}`}/>
            </button>;
        })}
    </div>;
}

export function Drawer({label, onClose, children}: {readonly label: string; readonly onClose: () => void; readonly children: ReactNode}) {
    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !event.defaultPrevented) onClose(); };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);
    return <>
        <button type="button" tabIndex={-1} aria-label={`Close ${label.toLowerCase()}`} onClick={onClose} className="fixed inset-0 z-40 cursor-default bg-scrim"/>
        <aside role="dialog" aria-modal="true" aria-label={label}
            className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[560px] flex-col bg-surface shadow-[-12px_0_32px_rgba(0,0,0,0.12)]">
            {children}
        </aside>
    </>;
}

export function CloseButton({onClose, autoFocus}: {readonly onClose: () => void; readonly autoFocus?: boolean}) {
    return <button type="button" aria-label="Close" autoFocus={autoFocus} onClick={onClose}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-secondary transition-colors hover:bg-zinc-100">
        <X size={16} className="!p-0"/>
    </button>;
}

export function BreadcrumbBar({items}: {readonly items: readonly {readonly label: string; readonly to?: string}[]}) {
    return <nav aria-label="Breadcrumb" className="border-b border-line">
        <div className="mx-auto flex min-h-[56px] w-full max-w-[1280px] items-center gap-2 px-5 text-sm sm:px-8">
            {items.map((item, index) => <Fragment key={item.label}>
                {index > 0 && <ChevronRight size={14} className="!p-0 shrink-0 text-ink-disabled"/>}
                {item.to
                    ? <Link to={item.to} className="text-ink-muted hover:text-ink">{item.label}</Link>
                    : <span aria-current="page" className="truncate font-semibold text-ink">{item.label}</span>}
            </Fragment>)}
        </div>
    </nav>;
}

export function WebhookTile() {
    return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-inverse-surface text-white"><Webhook size={20} className="!p-0"/></span>;
}
